export const MARKET_DATA_STATUS_READY = 'ready';
export const MARKET_DATA_STATUS_PENDING = 'pending_market_data';
export const MARKET_DATA_STATUS_RETRYING = 'retrying_market_data';
export const MARKET_DATA_STATUS_UNAVAILABLE = 'market_data_unavailable';
export const MARKET_DATA_STATUS_MISSING_PRICE = 'missing_price';
export const MARKET_DATA_STATUS_MISSING_ADTV = 'missing_adtv';
export const MARKET_DATA_STATUS_MISSING_PRICE_AND_ADTV = 'missing_price_and_adtv';

function formatSecurityCount(count) {
    return `${count} watchlist securit${count === 1 ? 'y' : 'ies'}`;
}

function getStatusObject(rowOrStatus) {
    if (!rowOrStatus) return null;
    if (rowOrStatus.market_data_status && typeof rowOrStatus.market_data_status === 'object') {
        return rowOrStatus.market_data_status;
    }
    if (
        Object.prototype.hasOwnProperty.call(rowOrStatus, 'status')
        && Object.prototype.hasOwnProperty.call(rowOrStatus, 'pos_sizing_status')
    ) {
        return rowOrStatus;
    }
    return null;
}

export function getMarketDataCoverage(payload) {
    return payload?.saved_draft_market_data_coverage
        ?? payload?.watchlist_market_data_coverage
        ?? null;
}

export function getActualPortfolioMarketDataStatus(rowOrStatus) {
    const status = getStatusObject(rowOrStatus);
    return status?.actual_portfolio_status ?? null;
}

export function hasActualPortfolioPriceGap(rowOrStatus) {
    const status = getActualPortfolioMarketDataStatus(rowOrStatus);
    return Boolean(status && status !== MARKET_DATA_STATUS_READY);
}

export function getActualPortfolioNoPriceTooltip(rowOrStatus) {
    switch (getActualPortfolioMarketDataStatus(rowOrStatus)) {
        case MARKET_DATA_STATUS_PENDING:
            return 'This security is still being processed for market-data coverage. Price fields stay blank unless coverage becomes available.';
        case MARKET_DATA_STATUS_RETRYING:
            return 'Market-data coverage is still retrying for this security after a transient issue. Price fields stay blank unless coverage becomes available.';
        case MARKET_DATA_STATUS_UNAVAILABLE:
            return 'Market data is currently unavailable for this security, so price fields stay blank for now.';
        case MARKET_DATA_STATUS_MISSING_PRICE_AND_ADTV:
            return 'This security is resolved, but stored price and liquidity coverage are still incomplete.';
        case MARKET_DATA_STATUS_MISSING_PRICE:
        default:
            return 'This security is still in the portfolio, but no stored price is currently available for it. Risk fields stay blank unless market-data coverage becomes available.';
    }
}

export function getWatchlistNoPriceTooltip(rowOrStatus) {
    switch (getActualPortfolioMarketDataStatus(rowOrStatus)) {
        case MARKET_DATA_STATUS_PENDING:
            return 'This security is still being processed for market-data coverage. The watchlist will update automatically if coverage becomes available.';
        case MARKET_DATA_STATUS_RETRYING:
            return 'Market-data coverage is still retrying for this security after a transient issue. The watchlist will update automatically if coverage becomes available.';
        case MARKET_DATA_STATUS_UNAVAILABLE:
            return 'Market data is currently unavailable for this security.';
        case MARKET_DATA_STATUS_MISSING_PRICE_AND_ADTV:
            return 'This security is resolved, but stored price and liquidity coverage are still incomplete.';
        case MARKET_DATA_STATUS_MISSING_PRICE:
        default:
            return 'No stored price is currently available for this security. The watchlist will update automatically if market-data coverage becomes available.';
    }
}

export function getMarketDataStatusCode(rowOrStatus) {
    if (!rowOrStatus) return null;
    if (typeof rowOrStatus === 'string') {
        return rowOrStatus;
    }
    if (typeof rowOrStatus.market_data_status_code === 'string') {
        return rowOrStatus.market_data_status_code;
    }
    const status = getStatusObject(rowOrStatus);
    if (status && typeof status.status === 'string') {
        return status.status;
    }
    return null;
}

export function isWatchlistMarketDataPending(rowOrStatus) {
    return getMarketDataStatusCode(rowOrStatus) === MARKET_DATA_STATUS_PENDING;
}

export function isWatchlistMarketDataRetrying(rowOrStatus) {
    return getMarketDataStatusCode(rowOrStatus) === MARKET_DATA_STATUS_RETRYING;
}

export function isWatchlistMarketDataUnavailable(rowOrStatus) {
    return getMarketDataStatusCode(rowOrStatus) === MARKET_DATA_STATUS_UNAVAILABLE;
}

export function getWatchlistMarketStatusLabel(rowOrStatus) {
    const code = getMarketDataStatusCode(rowOrStatus);
    switch (code) {
        case MARKET_DATA_STATUS_PENDING:
            return 'Searching…';
        case MARKET_DATA_STATUS_RETRYING:
            return 'Retrying…';
        case MARKET_DATA_STATUS_UNAVAILABLE:
            return 'Unavailable';
        case MARKET_DATA_STATUS_MISSING_PRICE:
        case MARKET_DATA_STATUS_MISSING_PRICE_AND_ADTV:
            return 'Status unavailable';
        case MARKET_DATA_STATUS_MISSING_ADTV:
            return 'Status unavailable';
        case MARKET_DATA_STATUS_READY:
            return null;
        default:
            return 'Status unavailable';
    }
}

export function getWatchlistMarketStatusColor(rowOrStatus) {
    const code = getMarketDataStatusCode(rowOrStatus);
    switch (code) {
        case MARKET_DATA_STATUS_PENDING:
            return 'info';
        case MARKET_DATA_STATUS_RETRYING:
            return 'warning';
        case MARKET_DATA_STATUS_UNAVAILABLE:
            return 'error';
        case MARKET_DATA_STATUS_MISSING_PRICE:
        case MARKET_DATA_STATUS_MISSING_PRICE_AND_ADTV:
        case MARKET_DATA_STATUS_MISSING_ADTV:
            return 'info';
        case MARKET_DATA_STATUS_READY:
            return 'success';
        default:
            return 'default';
    }
}

export function getWatchlistMarketStatusTooltip(rowOrStatus) {
    switch (getMarketDataStatusCode(rowOrStatus)) {
        case MARKET_DATA_STATUS_PENDING:
            return 'Market data is being fetched. This row will update automatically.';
        case MARKET_DATA_STATUS_RETRYING:
            return 'Market data is retrying after a temporary issue.';
        case MARKET_DATA_STATUS_UNAVAILABLE:
            return 'Market data is currently unavailable for this security.';
        case MARKET_DATA_STATUS_MISSING_PRICE_AND_ADTV:
        case MARKET_DATA_STATUS_MISSING_PRICE:
        case MARKET_DATA_STATUS_MISSING_ADTV:
            return 'Waiting for market status from the market-data service. Price and liquidity fields will update separately.';
        case MARKET_DATA_STATUS_READY:
            return null;
        default:
            return 'Waiting for market-data status from the price loader.';
    }
}

export function buildPosSizingMarketDataDetail(payload) {
    const coverage = getMarketDataCoverage(payload);
    const excludedCount = Number(
        coverage?.pos_sizing_excluded_count
        ?? payload?.watchlist_missing_market_data_count
        ?? 0
    );
    if (excludedCount <= 0) {
        return '';
    }

    const publicationEvidenceMissingCount = Number(
        coverage?.publication_evidence_missing_count || 0
    );
    const publicationReadinessUnavailableCount = Number(
        coverage?.publication_readiness_unavailable_count || 0
    );
    if (
        !coverage?.raw_lifecycle_state_available
        && publicationEvidenceMissingCount <= 0
        && publicationReadinessUnavailableCount <= 0
    ) {
        return (
            `${formatSecurityCount(excludedCount)} ${
                excludedCount === 1 ? 'is' : 'are'
            } currently excluded from pos sizing because required price or liquidity data is unavailable.`
        );
    }

    const detailParts = [];
    const pendingCount = Number(coverage?.pending_count || 0);
    const retryingCount = Number(coverage?.retrying_count || 0);
    const unavailableCount = Number(coverage?.unavailable_count || 0);
    const coverageGapCount = Number(coverage?.coverage_gap_count || 0);

    if (pendingCount > 0) {
        detailParts.push(
            `${formatSecurityCount(pendingCount)} ${
                pendingCount === 1 ? 'is' : 'are'
            } still processing for market-data coverage.`,
        );
    }
    if (retryingCount > 0) {
        detailParts.push(
            `${formatSecurityCount(retryingCount)} ${
                retryingCount === 1 ? 'is' : 'are'
            } retrying after a transient market-data issue.`,
        );
    }
    if (unavailableCount > 0) {
        detailParts.push(
            `${formatSecurityCount(unavailableCount)} ${
                unavailableCount === 1 ? 'is' : 'are'
            } currently unavailable or unresolved.`,
        );
    }
    if (coverageGapCount > 0) {
        detailParts.push(
            `${formatSecurityCount(coverageGapCount)} ${
                coverageGapCount === 1 ? 'is' : 'are'
            } resolved but still missing required price or liquidity fields.`,
        );
    }
    if (publicationEvidenceMissingCount > 0) {
        detailParts.push(
            `${formatSecurityCount(publicationEvidenceMissingCount)} ${
                publicationEvidenceMissingCount === 1 ? 'has' : 'have'
            } price and liquidity but lacks the selected beta or complete source evidence.`,
        );
    }
    if (publicationReadinessUnavailableCount > 0) {
        detailParts.push(
            `Complete Position Sizing readiness could not be verified for ${
                formatSecurityCount(publicationReadinessUnavailableCount)
            }; ${publicationReadinessUnavailableCount === 1 ? 'that security remains' : 'those securities remain'} excluded from synchronization.`,
        );
    }

    return [
        `${formatSecurityCount(excludedCount)} ${
            excludedCount === 1 ? 'is' : 'are'
        } currently excluded from pos sizing.`,
        ...detailParts,
    ].join(' ');
}

export function buildPosSizingMarketDataChips(payload, { infoOnly = false } = {}) {
    const coverage = getMarketDataCoverage(payload);
    const excludedCount = Number(
        coverage?.pos_sizing_excluded_count
        ?? payload?.watchlist_missing_market_data_count
        ?? 0
    );
    if (excludedCount <= 0) {
        return [];
    }

    const publicationEvidenceMissingCount = Number(
        coverage?.publication_evidence_missing_count || 0
    );
    const publicationReadinessUnavailableCount = Number(
        coverage?.publication_readiness_unavailable_count || 0
    );
    if (
        !coverage?.raw_lifecycle_state_available
        && publicationEvidenceMissingCount <= 0
        && publicationReadinessUnavailableCount <= 0
    ) {
        return [{
            label: `${excludedCount} Missing Market Data`,
            color: infoOnly ? 'default' : 'warning',
            variant: 'outlined',
        }];
    }

    const chips = [];
    const chipDefinitions = [
        { count: Number(coverage?.pending_count || 0), label: 'Pending Data', color: infoOnly ? 'default' : 'info' },
        { count: Number(coverage?.retrying_count || 0), label: 'Retrying Data', color: infoOnly ? 'default' : 'warning' },
        { count: Number(coverage?.unavailable_count || 0), label: 'Unavailable Data', color: infoOnly ? 'default' : 'warning' },
        { count: Number(coverage?.coverage_gap_count || 0), label: 'Coverage Gap', color: infoOnly ? 'default' : 'warning' },
        { count: publicationEvidenceMissingCount, label: 'Incomplete Sizing Data', color: infoOnly ? 'default' : 'warning' },
        { count: publicationReadinessUnavailableCount, label: 'Readiness Unknown', color: infoOnly ? 'default' : 'warning' },
    ];

    chipDefinitions.forEach(({ count, label, color }) => {
        if (count > 0) {
            chips.push({
                label: `${count} ${label}`,
                color,
                variant: 'outlined',
            });
        }
    });

    return chips.length > 0
        ? chips
        : [{
            label: `${excludedCount} Missing Market Data`,
            color: infoOnly ? 'default' : 'warning',
            variant: 'outlined',
        }];
}
